"use client"

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { useQuery } from '@tanstack/react-query'
import { useFormContext, useController, FieldPath, FieldValues } from 'react-hook-form'
import { useDirtyFields } from '../../context/dirty-fields-context'
import InputGroup from '@/shared/components/inputs/input-group'
import { Textarea } from '@/shared/components/ui/textarea'
import { useSession } from '@/features/auth/context/session.context'
import { useProjectNotesTagged, type TaggedVolunteer } from '../../context/project-notes-tagged-context'
import { Button } from '@/shared/components/ui/button'
import { ChevronDownIcon } from '@radix-ui/react-icons'
import { X } from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import { authFetchJson } from '@/shared/lib/auth-fetch'

type ProjectGeneralNotesVolunteerProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> = {
  name: TName
  inputGroupProps?: {
    label?: string
    wrapperClassName?: string
  }
  defaultRows?: number
  /** Optional action(s) to render on the same line as the label (e.g. filter icon) */
  labelAction?: React.ReactNode
}

function ProjectGeneralNotesVolunteer<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
  name,
  inputGroupProps,
  defaultRows = 4,
  labelAction,
}: ProjectGeneralNotesVolunteerProps<TFieldValues, TName>) {
  const { control } = useFormContext<TFieldValues>()
  const { isFieldDirty, isFormClean } = useDirtyFields()
  const isDirty = isFieldDirty(name as string)
  const { profile } = useSession()
  const { taggedVolunteers, setTaggedVolunteers } = useProjectNotesTagged()

  const { field, fieldState } = useController({
    control,
    name,
  })

  const { error } = fieldState
  const [newNote, setNewNote] = useState('')
  const [historicalNotes, setHistoricalNotes] = useState('')
  const [isNotesExpanded, setIsNotesExpanded] = useState(false)
  const previousFormCleanRef = useRef<boolean>(isFormClean)
  const previousNewNoteRef = useRef<string>('')
  const hasAddedDateStampRef = useRef<boolean>(false)
  const lastTypingTimeRef = useRef<number>(0)
  const initialNotesRef = useRef<string>('')
  const newNoteTextareaRef = useRef<HTMLTextAreaElement | null>(null)
  const pendingCursorRef = useRef<number | null>(null)
  const lastStampInsertedAtRef = useRef<number>(0)
  const [mentionOpen, setMentionOpen] = useState(false)
  const [mentionStart, setMentionStart] = useState(0)
  const [mentionEnd, setMentionEnd] = useState(0)
  const [mentionQuery, setMentionQuery] = useState('')
  const [mentionSelectedIndex, setMentionSelectedIndex] = useState(0)
  const [mentionDropdownRect, setMentionDropdownRect] = useState<{ top: number; left: number; width: number } | null>(null)
  const mentionListRef = useRef<HTMLUListElement | null>(null)

  const { data: teamLeadersData } = useQuery({
    queryKey: ['volunteers-team-leaders'],
    queryFn: async () => {
      const res = await authFetchJson<any[]>('/user/by-role?roleId=1,2,3,4')
      return res.success && res.data ? res.data : []
    },
  })

  const volunteerOptions = useMemo(() => {
    if (!Array.isArray(teamLeadersData)) return []
    const taggedIds = new Set(taggedVolunteers.map((v) => v.id))
    return teamLeadersData
      .filter((u: { id?: number; fullname?: string }) => u.id != null && !taggedIds.has(u.id))
      .map((u: { id: number; fullname?: string }) => ({ id: u.id, fullname: u.fullname || 'Unknown' }))
  }, [teamLeadersData, taggedVolunteers])

  const filteredMentionOptions = useMemo(() => {
    const q = mentionQuery.trim().toLowerCase()
    if (!q) return volunteerOptions.slice(0, 10)
    return volunteerOptions
      .filter((v) => v.fullname.toLowerCase().includes(q))
      .slice(0, 10)
  }, [volunteerOptions, mentionQuery])

  const addTagged = useCallback(
    (v: TaggedVolunteer) => {
      setTaggedVolunteers((prev) => [...prev, v])
      setMentionQuery('')
      setMentionOpen(false)
    },
    [setTaggedVolunteers]
  )

  const insertMentionAndAddTagged = useCallback(
    (v: TaggedVolunteer) => {
      const insert = `@${v.fullname} `
      const noteWithInsert = newNote.slice(0, mentionStart) + insert + newNote.slice(mentionEnd)
      setNewNote(noteWithInsert)
      previousNewNoteRef.current = noteWithInsert
      const newFullValue = historicalNotes ? `${historicalNotes}\n${noteWithInsert}` : noteWithInsert
      field.onChange(newFullValue)
      addTagged(v)
      const newCursor = mentionStart + insert.length
      pendingCursorRef.current = newCursor
    },
    [newNote, mentionStart, mentionEnd, historicalNotes, field, addTagged]
  )

  const removeTagged = useCallback(
    (id: number) => {
      setTaggedVolunteers((prev) => prev.filter((t) => t.id !== id))
    },
    [setTaggedVolunteers]
  )

  const updateMentionRect = useCallback(() => {
    if (!newNoteTextareaRef.current) return
    const rect = newNoteTextareaRef.current.getBoundingClientRect()
    setMentionDropdownRect({
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
    })
  }, [])

  useEffect(() => {
    if (!mentionOpen || !newNoteTextareaRef.current) {
      setMentionDropdownRect(null)
      return
    }
    updateMentionRect()
    window.addEventListener('scroll', updateMentionRect, true)
    window.addEventListener('resize', updateMentionRect)
    return () => {
      window.removeEventListener('scroll', updateMentionRect, true)
      window.removeEventListener('resize', updateMentionRect)
    }
  }, [mentionOpen, newNote, updateMentionRect])

  useEffect(() => {
    if (!mentionOpen || !mentionListRef.current) return
    const selected = mentionListRef.current.children[mentionSelectedIndex] as HTMLElement | undefined
    selected?.scrollIntoView({ block: 'nearest' })
  }, [mentionOpen, mentionSelectedIndex])

  // Show expand button whenever there are notes so users can expand to read (especially on mobile)
  const shouldShowExpandButton = !!historicalNotes?.trim()

  // Format date as MM/DD/YY with user name
  const formatDateStamp = React.useCallback(() => {
    const now = new Date()
    const month = String(now.getMonth() + 1).padStart(2, '0')
    const day = String(now.getDate()).padStart(2, '0')
    const year = String(now.getFullYear()).slice(-2)
    const userName = profile?.fullname || 'Unknown User'
    return `${month}/${day}/${year} ${userName}:`
  }, [profile])

  // Initialize: set historical notes from field value on mount
  useEffect(() => {
    const fullNotes = field.value || ''
    if (fullNotes && !initialNotesRef.current) {
      initialNotesRef.current = fullNotes
      setHistoricalNotes(fullNotes)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Only run on mount - field.value intentionally excluded to prevent re-initialization

  // Reset and update historical notes when form is saved
  useEffect(() => {
    if (previousFormCleanRef.current === false && isFormClean === true) {
      // Form was just saved - reset flag and clear new note
      hasAddedDateStampRef.current = false
      lastTypingTimeRef.current = 0
      setNewNote('')
      previousNewNoteRef.current = ''
      
      // Update historical notes to include the newly saved note
      const fullNotes = field.value || ''
      setHistoricalNotes(fullNotes)
      initialNotesRef.current = fullNotes
    }
    previousFormCleanRef.current = isFormClean
  }, [isFormClean, field.value])

  // Restore cursor after we insert stamp on Enter
  useEffect(() => {
    const pos = pendingCursorRef.current
    if (pos === null) return
    pendingCursorRef.current = null
    const el = newNoteTextareaRef.current
    if (!el) return
    const raf = requestAnimationFrame(() => {
      if (pos >= 0 && pos <= newNote.length) {
        el.focus()
        el.setSelectionRange(pos, pos)
      }
    })
    return () => cancelAnimationFrame(raf)
  }, [newNote])

  const handleNewNoteFocus = () => {
    if ((newNote || '').trim() === '') {
      const stamp = formatDateStamp() + ' '
      previousNewNoteRef.current = stamp
      setNewNote(stamp)
      const newFullValue = historicalNotes ? `${historicalNotes}\n${stamp}` : stamp
      field.onChange(newFullValue)
      const newCursorPos = stamp.length
      pendingCursorRef.current = newCursorPos
    }
  }

  // Intercept Enter: insert stamp on new line, unless cursor is already on an empty stamped line (avoids duplicate stamp).
  const handleNewNoteKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (mentionOpen && filteredMentionOptions.length > 0) {
      if (e.key === 'Escape') {
        e.preventDefault()
        setMentionOpen(false)
        return
      }
      if (e.key === 'Enter') {
        e.preventDefault()
        const v = filteredMentionOptions[mentionSelectedIndex]
        if (v) insertMentionAndAddTagged(v)
        return
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setMentionSelectedIndex((i) =>
          i < filteredMentionOptions.length - 1 ? i + 1 : 0
        )
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setMentionSelectedIndex((i) =>
          i > 0 ? i - 1 : filteredMentionOptions.length - 1
        )
        return
      }
    }
    if (e.key !== 'Enter') return
    const el = newNoteTextareaRef.current
    const currentValue = newNote
    const start = el?.selectionStart ?? currentValue.length
    const end = el?.selectionEnd ?? start
    const hasContent = currentValue.trim().length > 0
    if (!el || !hasContent) return
    const dateStamp = formatDateStamp()
    const before = currentValue.slice(0, start)
    const after = currentValue.slice(end)
    const lastNewline = before.lastIndexOf('\n')
    const currentLine = lastNewline === -1 ? before : before.slice(lastNewline + 1)
    const isEmptyStampedLine = currentLine === dateStamp || currentLine === dateStamp + ' '

    e.preventDefault()
    if (isEmptyStampedLine) {
      requestAnimationFrame(() => {
        const ta = newNoteTextareaRef.current
        if (ta) ta.scrollTop = ta.scrollHeight - ta.clientHeight
      })
      return
    }
    const noteWithDate = `${before}\n${dateStamp} ${after}`
    lastStampInsertedAtRef.current = Date.now()
    hasAddedDateStampRef.current = true
    previousNewNoteRef.current = noteWithDate
    lastTypingTimeRef.current = Date.now()
    const newCursorPos = before.length + 1 + dateStamp.length + 1
    pendingCursorRef.current = newCursorPos
    setNewNote(noteWithDate)
    const newFullValue = historicalNotes ? `${historicalNotes}\n${noteWithDate}` : noteWithDate
    field.onChange(newFullValue)
  }

  // Handle new note input change. Stamp is added only on Enter (in onKeyDown) to avoid
  // mobile bug where each keystroke was treated as "first input" and got a new stamp.
  const handleNewNoteChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value
    const start = e.target.selectionStart ?? value.length
    const previousValue = previousNewNoteRef.current
    const isTyping = value.length > previousValue.length
    const newText = value.slice(previousValue.length)

    // Enter is handled in onKeyDown; if we see newline-only here (e.g. from paste), pass through
    const isNewlineOnly = /^[\r\n]+$/.test(newText) && newText.length > 0
    if (isTyping && isNewlineOnly) {
      previousNewNoteRef.current = value
      setNewNote(value)
      const newFullValue = historicalNotes ? `${historicalNotes}\n${value}` : value
      field.onChange(newFullValue)
      return
    }

    if (isTyping) lastTypingTimeRef.current = Date.now()

    setNewNote(value)
    const noteToAdd = value.trim().length > 0 ? value : ''
    const newFullValue = historicalNotes && noteToAdd
      ? `${historicalNotes}\n${noteToAdd}`
      : noteToAdd || historicalNotes || ''
    previousNewNoteRef.current = value
    field.onChange(newFullValue)

    const beforeCursor = value.slice(0, start)
    const lastAt = beforeCursor.lastIndexOf('@')
    const hasSpaceAfterAt = lastAt >= 0 && beforeCursor.slice(lastAt + 1).includes(' ')
    if (lastAt >= 0 && !hasSpaceAfterAt) {
      setMentionStart(lastAt)
      setMentionEnd(start)
      setMentionQuery(beforeCursor.slice(lastAt + 1))
      setMentionOpen(true)
      setMentionSelectedIndex(0)
    } else {
      setMentionOpen(false)
    }
  }

  return (
    <div className="space-y-4 xl:col-span-2">
      {/* Project General Notes - read-only with accordion */}
      <div className="space-y-2">
        {/* Label + Type @ + icons on left; expand/collapse arrow right-justified on small screens */}
        <div className="flex flex-nowrap items-center gap-2 w-full min-w-0">
          <div className="flex flex-wrap items-center gap-2 min-w-0 flex-1">
            <label className={cn(
              "text-sm pl-1 transition-colors duration-200 shrink-0",
              isDirty && "text-[#9c27b0]"
            )}>
              {inputGroupProps?.label || "Project General Notes"}
            </label>
            {labelAction}
          </div>
          {shouldShowExpandButton && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setIsNotesExpanded(!isNotesExpanded)}
              className="h-6 w-6 p-0 shrink-0 ml-auto text-muted-foreground hover:text-foreground"
              aria-label={isNotesExpanded ? 'Collapse notes' : 'Expand notes to read more'}
            >
              <ChevronDownIcon className={cn(
                "h-4 w-4 transition-transform duration-200",
                isNotesExpanded && "rotate-180"
              )} />
            </Button>
          )}
        </div>
        
        <InputGroup 
          wrapperClassName={inputGroupProps?.wrapperClassName || "xl:col-span-2"}
          error={error?.message}
          isDirty={isDirty}
        >
          <div className={cn(
            "rounded-md border border-input bg-muted/50 p-3 overflow-y-auto transition-all duration-200",
            isNotesExpanded ? "min-h-[60px] max-h-[200px]" : "min-h-[60px] max-h-[80px]"
          )}>
            {historicalNotes ? (
              <pre className="whitespace-pre-wrap text-sm text-muted-foreground font-sans">
                {historicalNotes}
              </pre>
            ) : (
              <p className="text-sm text-muted-foreground italic">No notes yet</p>
            )}
          </div>
        </InputGroup>
      </div>
      
      {taggedVolunteers.length > 0 && (
        <div className="flex flex-wrap gap-1.5 items-center">
          <span className="text-xs text-muted-foreground">Notify on save:</span>
          {taggedVolunteers.map((v) => (
            <span
              key={v.id}
              className="inline-flex items-center gap-1 rounded-md bg-primary/10 text-primary px-2 py-0.5 text-sm"
            >
              {v.fullname}
              <button
                type="button"
                aria-label={`Remove ${v.fullname} from tag list`}
                className="hover:bg-primary/20 rounded p-0.5"
                onClick={() => removeTagged(v.id)}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      {/* Add New Note section - editable for Volunteers */}
      <div className="relative">
        <InputGroup 
          label="Add New Note" 
          wrapperClassName={inputGroupProps?.wrapperClassName || "xl:col-span-2"}
        >
          <Textarea
            ref={newNoteTextareaRef}
            value={newNote}
            onChange={handleNewNoteChange}
            onKeyDown={handleNewNoteKeyDown}
            onFocus={handleNewNoteFocus}
            onBlur={() => {
              setMentionOpen(false)
              field.onBlur()
            }}
            placeholder="Add dated notes... (type @ to tag a volunteer)"
            rows={defaultRows}
            className="resize-none"
          />
        </InputGroup>
        {mentionOpen &&
          mentionDropdownRect &&
          typeof document !== 'undefined' &&
          createPortal(
            <div
              className="fixed z-[100] rounded-md border border-input bg-popover text-popover-foreground shadow-md overflow-hidden"
              role="listbox"
              style={{
                top: mentionDropdownRect.top,
                left: mentionDropdownRect.left,
                width: mentionDropdownRect.width,
                minWidth: 200,
              }}
            >
              <div className="p-2 max-h-52 overflow-y-auto">
                {filteredMentionOptions.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-2 px-2">
                    {volunteerOptions.length === 0
                      ? taggedVolunteers.length > 0
                        ? 'All volunteers are already tagged.'
                        : 'No volunteers available.'
                      : `No match for "${mentionQuery.trim() || '@'}"`}
                  </p>
                ) : (
                  <ul ref={mentionListRef} className="space-y-0.5">
                    {filteredMentionOptions.map((v, i) => (
                      <li key={v.id}>
                        <button
                          type="button"
                          role="option"
                          aria-selected={i === mentionSelectedIndex}
                          className={`w-full text-left text-sm py-2 px-3 rounded ${
                            i === mentionSelectedIndex ? 'bg-muted' : 'hover:bg-muted/70'
                          }`}
                          onMouseDown={(e) => {
                            e.preventDefault()
                            insertMentionAndAddTagged(v)
                          }}
                        >
                          {v.fullname}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>,
            document.body
          )}
      </div>
    </div>
  )
}

export default ProjectGeneralNotesVolunteer
